import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
const SERVER_BASE_URL = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || '';

// Функция для получения базового URL API для виджетов
// Приоритет: 1. Переданный apiUrl, 2. VITE_API_URL без /api/v1, 3. window.location.origin
export function getApiBaseURL(customApiUrl) {
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('SERVER_BASE_URL:', SERVER_BASE_URL);
  console.log('import.meta.env.VITE_API_URL:', import.meta.env.VITE_API_URL);
  console.log('window.location.origin:', window.location.origin);
  if (customApiUrl) {
    return customApiUrl;
  }
  if (SERVER_BASE_URL) {
    return SERVER_BASE_URL;
  }
  // Если VITE_API_URL установлен, используем его без /api/v1
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.replace('/api/v1', '');
  }
  return window.location.origin;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000,
  // По умолчанию не шлём cookies везде подряд (это часто ломает CORS при кросс-доменных запросах).
  // Для login/me/logout и т.п. включаем credentials точечно на уровне конкретного запроса.
  withCredentials: true,
});

// Добавляем интерцептор для автоматической отправки токена через заголовок
api.interceptors.request.use(
  (config) => {

    // Пытаемся получить токен из localStorage как fallback
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Добавляем интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);

    // Если получили 401, можно добавить перенаправление на страницу входа
    if (error.response?.status === 401) {
      // Токен теперь управляется через cookies автоматически
    }
    
    return Promise.reject(error);
  }
);

export { SERVER_BASE_URL };

export const apiService = {
  // Clothes API
  async getClothes(page = 1, limit = 250) {
    try {
      const url = `/clothes/list?page=${page}&limit=${limit}`;
      console.log('Fetching clothes from:', API_BASE_URL + url);
      const response = await api.get(url);
      console.log('API Response:', response.data);
      
      // Преобразуем данные из API в формат, ожидаемый фронтендом
      if (response.data && response.data.items) {
        const transformedItems = response.data.items.map(item => ({
          _id: item.id,
          name: item.name,
          description: item.description,
          category: item.category,
          prompt: item.prompt,
          website_url: item.website_url,
          front_image_url: item.front_image_url,
          back_image_url: item.back_image_url,
          created_at: item.created_at,
          updated_at: item.updated_at
        }));
        console.log('Transformed items:', transformedItems);
        
        // Возвращаем объект с items и total
        return {
          items: transformedItems,
          total: response.data.total || transformedItems.length
        };
      }
      console.log('No items found in response');
      return { items: [], total: 0 };
    } catch (error) {
      console.error('Error fetching clothes:', error);
      console.error('Error details:', error.response?.data);
      return { items: [], total: 0 };
    }
  },

  async getCloth(id) {
    const response = await api.get(`/clothes/${id}`);
    return response.data;
  },

  async createCloth(formData) {
    console.log('Creating cloth with formData:', formData);
    // Логируем содержимое FormData
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    
    // Используем fetch для multipart/form-data
    const response = await fetch(`${API_BASE_URL}/clothes`, {
      method: 'POST',
      body: formData,
      credentials: 'include' // Включаем отправку cookies
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    
    return await response.text(); // Возвращаем ID как строку
  },

  async updateCloth(id, formData) {
    // Используем fetch для multipart/form-data
    const response = await fetch(`${API_BASE_URL}/clothes/${id}`, {
      method: 'PATCH',
      body: formData,
      credentials: 'include' // Включаем отправку cookies
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    
    return await response.text();
  },

  async deleteCloth(id) {
    const response = await api.delete(`/clothes/${id}`);
    return response.data;
  },

  // Возвращает список названий листов xlsx-файла (для экрана выбора листов
  // перед импортом). Для CSV листов нет - вызывать не нужно.
  async getXLSXSheets(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/clothes/import/xlsx-sheets`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json(); // { sheets: [...] }
  },

  // Clothes catalog import (CSV/XLSX). sheets - опциональный список названий
  // листов xlsx для импорта (пусто/не передано - все листы книги).
  async importClothes(file, sheets) {
    const formData = new FormData();
    formData.append('file', file);
    if (sheets && sheets.length > 0) {
      formData.append('sheets', sheets.join(','));
    }

    // fetch, а не axios: загрузка асинхронная (сервер отвечает сразу после
    // разбора файла), но сам файл может быть большим - не хотим упираться
    // в axios-таймаут (10с), как и в остальных multipart-запросах этого файла.
    const response = await fetch(`${API_BASE_URL}/clothes/import`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json(); // { run_id, status, total_rows }
  },

  async getImportRun(runId) {
    const response = await api.get(`/clothes/import/runs/${runId}`);
    return response.data;
  },

  async getImportRuns(page = 1, limit = 20) {
    const response = await api.get(`/clothes/import/runs?page=${page}&limit=${limit}`);
    return response.data;
  },

  // Virtual Try-On API
  async startVirtualTryOn(formData) {
    const response = await api.post('/virtual-try-on', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // OpenRouter Virtual Try-On API
  async startOpenRouterTryOn(formData) {
    const response = await fetch(`${API_BASE_URL}/virtual-try-on/open-router`, {
      method: 'POST', 
      body: formData,
      credentials: 'include' // Включаем отправку cookies
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  },

  // API Request History
  async getAPIRequestHistory() {
    try {
      const response = await api.get('/api-requests/history');
      return response.data.items || [];
    } catch (error) {
      console.error('Error fetching API request history:', error);
      return [];
    }
  },

  // Account API
  async login(email, password) {
    try {
      const response = await api.post(
        '/account/login',
        {
          email,
          password,
        },
        { withCredentials: true }
      );
      
      // Сохраняем токен в localStorage как fallback
      if (response.data.body && response.data.body.id) {
        // Извлекаем токен из заголовка Authorization
        const authHeader = response.headers.authorization;
        if (authHeader) {
          localStorage.setItem('authToken', authHeader);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  },

  async logout() {
    try {
      const response = await api.post('/account/logout', null, { withCredentials: true });
      
      // Удаляем токен из localStorage
      localStorage.removeItem('authToken');
      
      return response.data;
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  },

  async getMe() {
    try {
      const response = await api.get('/account/me', { withCredentials: true });
      console.log('getMe response:', response);
      console.log('getMe response.data:', response.data);
      console.log('getMe response.headers:', response.headers);
      return response.data;
    } catch (error) {
      console.error('Get me failed:', error);
      console.error('Get me error response:', error.response);
      throw error;
    }
  },

  async changePassword(oldPassword, newPassword) {
    try {
      const response = await api.post('/account/password-change', {
        old_password: oldPassword,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Change password failed:', error);
      throw error;
    }
  },

  // Admin only functions
  async createAccount(accountData) {
    try {
      const response = await api.post('/account', accountData);
      return response.data;
    } catch (error) {
      console.error('Create account failed:', error);
      throw error;
    }
  },

  async getAccountList() {
    try {
      const response = await api.get('/account/list');
      return response.data;
    } catch (error) {
      console.error('Get account list failed:', error);
      throw error;
    }
  },

  async getAccount(id) {
    try {
      const response = await api.get(`/account/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get account failed:', error);
      throw error;
    }
  },

  async updateAccount(id, accountData) {
    try {
      const response = await api.put(`/account/${id}`, accountData);
      return response.data;
    } catch (error) {
      console.error('Update account failed:', error);
      throw error;
    }
  },

  async deleteAccount(id) {
    try {
      const response = await api.delete(`/account/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete account failed:', error);
      throw error;
    }
  },

  // API Key management
  async generateAPIKey() {
    try {
      const response = await api.post('/account/api-key/generate');
      return response.data.body?.api_key || response.data.api_key;
    } catch (error) {
      console.error('Generate API key failed:', error);
      throw error;
    }
  },

  async generateAPIKeyForAccount(accountId) {
    try {
      const response = await api.post(`/account/${accountId}/api-key/generate`);
      return response.data.body?.api_key || response.data.api_key;
    } catch (error) {
      console.error('Generate API key for account failed:', error);
      throw error;
    }
  },

  // Chat API
  async sendChatMessage(message, model) {
    try {
      const response = await api.post('/chat', {
        message: message,
        model: model || undefined
      });
      return response.data.body || response.data;
    } catch (error) {
      console.error('Chat message failed:', error);
      throw error;
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      return { status: 'error', message: error.message };
    }
  },
};

export default apiService;
